import consentCheck from '@capsule/consent-check'
import checkWhenSlotRenderEnded from 'shared/assets/scripts/check-when-slot-render-ended'
import PmcBridge from 'shared/services/pmc-bridge'

// Define types for ad information and ads structure
type AdInfo = {
	elementId: string | null
}

type Ads = {
	bannerTop?: AdInfo
}

// Class to manage custom rules for ads displayed by coreAds
export default class AdsRules {
	private bannerTopElement: HTMLElement | null // Reference to the top banner ad element
	private ads: Ads // Object to store ad-related data

	// Initialize default properties and bind methods
	constructor() {
		this.bannerTopElement = document.querySelector('.ad-banniereHaute .ads-core-placer')
		this.ads = {}
		this.callbackSlotRender = this.callbackSlotRender.bind(this)
		this.onAdsRender = this.onAdsRender.bind(this)
	}

	// Initialize ads custom rules
	init(): void {
		this.setAds()
		if (this.bannerTopElement) this.getBannerTopData()
		this.addGoogleTagListener()
	}

	/**
	 * Sets ads if the user has given consent
	 */
	private setAds() {
		// Check for all consent or ads-specific consent before enabling ads
		consentCheck('allConsentGiven', () => {
			this.enableAds()
		})
		consentCheck('adsConsentGiven', () => {
			this.enableAds()
		})
	}

	/**
	 * Enables ads by initializing required scripts and settings
	 */
	private enableAds(): void {
		if (document.body.getAttribute('data-puboff') === 'true') return

		this.setUserPaidStatusInStorage()
	}

	/**
	 * Sets the user's paid status in local storage to disable ads if applicable
	 */
	private async setUserPaidStatusInStorage(): Promise<void> {
		const pmcBridge = new PmcBridge()
		Promise.all([pmcBridge.hasPassPrisma(), pmcBridge.hasSubscription()]).then(
			([hasPassPrisma, hasSubscription]) => {
				const isNoAdsUser = hasPassPrisma || hasSubscription
				const owsStorage = localStorage.getItem('ows-storage')
				let parsedOwsStorage = {}

				if (owsStorage) {
					parsedOwsStorage = JSON.parse(owsStorage)
				}
				Object.assign(parsedOwsStorage, { userNoAds: isNoAdsUser })

				localStorage.setItem('ows-storage', JSON.stringify(parsedOwsStorage))
			}
		)
	}

	/**
	 * Retrieve data for the top banner ad
	 */
	private getBannerTopData(): void {
		this.ads.bannerTop = {
			elementId: this.bannerTopElement?.getAttribute('id') || null
		}
	}

	/**
	 * Add a listener for Google Tag Manager slot rendering events
	 */
	private addGoogleTagListener(): void {
		checkWhenSlotRenderEnded({ callback: this.callbackSlotRender })
	}

	/**
	 * Callback function executed after each ad slot rendering
	 * @param _event The event object (unused but required by callback signature)
	 * @param elementId The ID of the rendered ad slot
	 */
	private callbackSlotRender(_event: object, elementId: string): void {
		this.onAdsRender(elementId)
	}

	/**
	 * Add a class to the ad placeholder's parent element
	 * Excludes ads inside draft content
	 * @param elementId The AdPlaceholder element ID
	 */
	private onAdsRender(elementId: string): void {
		const adPlaceholder = this.getAdPlaceholder(elementId)
		if (adPlaceholder) this.setAdPlaceholderLoaded(adPlaceholder)
	}

	/**
	 * Retrieve the ad placeholder element by its ID
	 * @param elementId The ID of the ad placeholder
	 * @returns The closest containerAds element or null
	 */
	private getAdPlaceholder(elementId: string): HTMLElement | null {
		return document.getElementById(elementId)?.closest('.containerAds') || null
	}

	/**
	 * Mark the ad placeholder as loaded by adding a class
	 * @param adPlaceholder The ad placeholder element
	 */
	private setAdPlaceholderLoaded(adPlaceholder: HTMLElement): void {
		adPlaceholder.classList.add('adLoaded')
	}
}
